import React, { useContext, useState, KeyboardEvent, useEffect } from 'react'
import { GridItem } from '@zuto/core/src/grid'
import { RadioButton } from '@zuto/atoms'
import { useCallbackOnce } from '@zuto/utilities'
import QuestionComponentProps from '../../../Questions/question-component-props'
import QuestionKey from '../../../Questions/question-key'
import { ApplicationFormContext } from '../../ApplicationForm/ApplicationFormContext'
import AppFormQuestion from '../../AppFormQuestion'
import withQuestion from '../../HOC/withQuestion'
import withFade from '../../HOC/withFade'
import {
  trackMultipleInputChoice,
  determineRadioVariant,
  handlePrevButtonClick,
  questionFontStyle
} from '../utils'
import NavigationButtons from '../../NavigationButtons/NavigationButtons'
import MaritalStatusOptions from './MaritalStatusOptions'
import { PrefillReviewPageApplicationFormContext } from '../../ApplicationForm/PrefillReviewPageApplicationFormContext'
import ToggleButtonGroup from '@zuto/atoms/src/Mui/ToogleButtonGroup/ToggleButtonGroup'
import { useAutoAdvance } from '../../../hooks/useAutoAdvance'

const MaritalStatusQuestion = ({
  existingAnswers,
  disablePrevious,
  updateFollowingPrefillStep
}: QuestionComponentProps) => {
  const { setQuestionName, hasNavigatedFromRequiredClick } = useContext(PrefillReviewPageApplicationFormContext)
  const appFormContext = useContext(ApplicationFormContext)
  const { featureSwitches } = appFormContext
  const {
    removeContinueCtaV1,
    reviewPageV3,
    questionCopyChangeV1,
    msmThemeV1
  } = featureSwitches ?? {}
  const answerInitValue = !removeContinueCtaV1 ? 'single' : ''
  const [answer, setAnswer] = useState(existingAnswers || answerInitValue)
  const [nextButtonCb] = useCallbackOnce()
  const [previousButtonCb] = useCallbackOnce()
  const autoAdvance = useAutoAdvance(QuestionKey.MARITAL_STATUS)

  useEffect(() => {
    // Used for logging purposes in PrefillReviewPageApplicationForm since questionConfig is not available
    if (reviewPageV3) {
      setQuestionName('maritalStatus')
    }
  }, [])

  const nextQuestion = () => {
    trackMultipleInputChoice({ label: `maritalStatus+${answer}` })
    appFormContext.dispatch({
      type: 'QUESTION_ANSWERED',
      name: QuestionKey.MARITAL_STATUS,
      value: answer
    })

    // Used for Navigation on Prefill Review Page
    if (updateFollowingPrefillStep) {
      if (reviewPageV3 && hasNavigatedFromRequiredClick) {
        updateFollowingPrefillStep(true, true, false)
      }
      if (!hasNavigatedFromRequiredClick) {
        updateFollowingPrefillStep(true, false, false)
      }
    }
  }

  const previousQuestion = () => {
    handlePrevButtonClick(
      appFormContext.dispatch,
      { name: QuestionKey.MARITAL_STATUS, value: answer }
    )
    if (updateFollowingPrefillStep) {
      if (hasNavigatedFromRequiredClick) {
        updateFollowingPrefillStep(true, true, false)
      }
      if (!hasNavigatedFromRequiredClick) {
        updateFollowingPrefillStep(false, false, false)
      }
    }
  }

  const handleEnterKeyPress = (e: KeyboardEvent) => {
    if (e.code === 'Enter' && answer) {
      nextButtonCb(nextQuestion)
    }
  }

  const updateAnswer = (value: string) => {
    setAnswer(value)
    if (removeContinueCtaV1) {
      autoAdvance(value)
    }
  }

  const renderOptions = () => {
    if (msmThemeV1) {
      return (
        <ToggleButtonGroup
          options={MaritalStatusOptions}
          id="marital-options"
          defaultState={answer}
          valueChanged={(value) => updateAnswer(value.toString())}
        />
      )
    }
    return (
      <RadioButton
        options={MaritalStatusOptions}
        id="marital-options"
        direction="vertical"
        mb={6}
        mt={3}
        valueChanged={(value) => updateAnswer(value.toString())}
        onKeyPress={handleEnterKeyPress}
        defaultState={answer}
        variant={determineRadioVariant({
          smallRadiosSideBySide: false
        })}
        alignment="center"
        removeContinueCta={removeContinueCtaV1}
      />
    )
  }

  return (
    <>
      <GridItem
        gridColumn={['auto / span 12', '3 / span 8']}
        minHeight="174px"
      >
        <AppFormQuestion
          questionKey={QuestionKey.MARITAL_STATUS}
          mt={3}
          textStyle={questionFontStyle}
        >
          {questionCopyChangeV1 ? 'What\'s your marital status?' : 'How would you describe yourself?'}
        </AppFormQuestion>
        {renderOptions()}
      </GridItem>
      <NavigationButtons
        nextHandler={() => nextButtonCb(nextQuestion)}
        previousHandler={() => previousButtonCb(previousQuestion)}
        disableNext={!answer}
        disablePrevious={disablePrevious ? 'invisible' : undefined}
        hideNext={removeContinueCtaV1}
      />
    </>
  )
}

export default withFade(withQuestion(MaritalStatusQuestion))
